import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../apiConfig';

export default function CentersManager({ onSubmitting }) {
    const [centersDetails, setCentersDetails] = useState({});
    const [selectedSlug, setSelectedSlug] = useState('rathnpura-deputy');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    // Center Details Form State
    const [centerMeta, setCenterMeta] = useState({ title: '', phone: '', email: '' });

    // Officer Form State
    const [editingOfficerId, setEditingOfficerId] = useState(null);
    const [officerForm, setOfficerForm] = useState({ name: '', position: '', phone: '', image: '' });
    const [imagePreview, setImagePreview] = useState('');

    // New Center Modal / Form State
    const [showNewCenterModal, setShowNewCenterModal] = useState(false);
    const [newCenterForm, setNewCenterForm] = useState({ title: '', slug: '', phone: '', email: '' });

    // Search filter for officers
    const [officerSearch, setOfficerSearch] = useState('');

    // Fetch all center details on load
    useEffect(() => {
        const fetchAllDetails = async () => {
            setLoading(true);
            try {
                const res = await fetch(getApiUrl(`api_center_details.php?t=${Date.now()}`));
                if (!res.ok) throw new Error('API server error');
                const data = await res.json();

                if (data && typeof data === 'object' && Object.keys(data).length > 0) {
                    setCentersDetails(data);
                    if (data['rathnpura-deputy']) {
                        setSelectedSlug('rathnpura-deputy');
                    } else {
                        setSelectedSlug(Object.keys(data)[0]);
                    }
                } else {
                    // Fallback to static public/centers_details.json
                    const fbRes = await fetch('/centers_details.json');
                    if (fbRes.ok) {
                        const fbData = await fbRes.json();
                        setCentersDetails(fbData);
                        setSelectedSlug('rathnpura-deputy');
                    }
                }
            } catch (err) {
                console.error('Fetch center details error:', err);
                const local = localStorage.getItem('site_centers_details');
                if (local) {
                    try {
                        const parsed = JSON.parse(local);
                        setCentersDetails(parsed);
                    } catch (e) { }
                }
                setMessage('⚠ සර්වර් දත්ත සම්බන්ධතාව නොමැත. Local දත්ත පෙන්වයි.');
            } finally {
                setLoading(false);
            }
        };

        fetchAllDetails();
    }, []);

    // When selectedSlug changes, populate centerMeta and reset officerForm
    useEffect(() => {
        if (selectedSlug && centersDetails[selectedSlug]) {
            const cur = centersDetails[selectedSlug];
            setCenterMeta({
                title: cur.title || '',
                phone: cur.phone || '',
                email: cur.email || ''
            });
            handleCancelOfficerEdit();
        }
    }, [selectedSlug, centersDetails]);

    // Save helper to API & LocalStorage
    const syncToServer = async (updatedMap) => {
        if (onSubmitting) onSubmitting(true);
        try {
            localStorage.setItem('site_centers_details', JSON.stringify(updatedMap));
            const res = await fetch(getApiUrl('api_center_details.php'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedMap)
            });
            if (!res.ok) throw new Error('Server returned ' + res.status);
            return true;
        } catch (err) {
            console.error('Center details sync error:', err);
            return false;
        } finally {
            if (onSubmitting) onSubmitting(false);
        }
    };

    // Save Center Contact Information (Title, Phone, Email)
    const handleSaveCenterMeta = async (e) => {
        if (e) e.preventDefault();
        if (!selectedSlug || !centersDetails[selectedSlug]) return;

        const current = centersDetails[selectedSlug];
        const updatedCenter = {
            ...current,
            title: centerMeta.title.trim(),
            phone: centerMeta.phone.trim(),
            email: centerMeta.email.trim()
        };

        const updatedMap = {
            ...centersDetails,
            [selectedSlug]: updatedCenter
        };

        setCentersDetails(updatedMap);
        const ok = await syncToServer(updatedMap);
        if (ok) {
            setMessage('✅ ආයතනයේ සම්බන්ධතා තොරතුරු සාර්ථකව යාවත්කාලීන විය!');
            setTimeout(() => setMessage(''), 4000);
        } else {
            setMessage('⚠ Local දත්ත සුරැකිණි, නමුත් සර්වර් එකට Save වීමේ දෝෂයක් ඇත.');
        }
    };

    // Image Upload Handler
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                setMessage('⚠ Image size should be less than 2MB');
                return;
            }
            if (!file.type.startsWith('image/')) {
                setMessage('⚠ Please select a valid image file');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setOfficerForm(prev => ({ ...prev, image: base64String }));
                setImagePreview(base64String);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setOfficerForm(prev => ({ ...prev, image: '' }));
        setImagePreview('');
        const fileInput = document.getElementById('officerPhotoUpload');
        if (fileInput) fileInput.value = '';
    };

    // Officer Edit Click
    const handleEditOfficerClick = (officer) => {
        setEditingOfficerId(officer.id);
        setOfficerForm({
            name: officer.name || '',
            position: officer.position || '',
            phone: officer.phone || '',
            image: officer.image || ''
        });
        setImagePreview(officer.image || '');
        window.scrollTo({ top: 350, behavior: 'smooth' });
    };

    const handleCancelOfficerEdit = () => {
        setEditingOfficerId(null);
        setOfficerForm({ name: '', position: '', phone: '', image: '' });
        setImagePreview('');
        const fileInput = document.getElementById('officerPhotoUpload');
        if (fileInput) fileInput.value = '';
    };

    // Add or Update Officer
    const handleSaveOfficer = async (e) => {
        if (e) e.preventDefault();

        if (!officerForm.name.trim() || !officerForm.position.trim()) {
            setMessage('⚠ කරුණාකර නිලධාරියාගේ නම සහ තනතුර ඇතුළත් කරන්න (Name & Position required)');
            return;
        }

        const currentCenter = centersDetails[selectedSlug] || {
            slug: selectedSlug,
            title: centerMeta.title || selectedSlug,
            phone: centerMeta.phone,
            email: centerMeta.email,
            officers: []
        };

        const currentOfficers = currentCenter.officers || [];
        let updatedOfficers;

        if (editingOfficerId !== null) {
            // Update existing officer
            updatedOfficers = currentOfficers.map(off => {
                if (String(off.id) === String(editingOfficerId)) {
                    return {
                        ...off,
                        name: officerForm.name.trim(),
                        position: officerForm.position.trim(),
                        phone: officerForm.phone.trim(),
                        image: officerForm.image || off.image || ''
                    };
                }
                return off;
            });
        } else {
            // Add new officer
            const newOfficer = {
                id: `${selectedSlug}-off-${Date.now()}`,
                name: officerForm.name.trim(),
                position: officerForm.position.trim(),
                phone: officerForm.phone.trim(),
                image: officerForm.image || ''
            };
            updatedOfficers = [...currentOfficers, newOfficer];
        }

        const updatedCenter = {
            ...currentCenter,
            officers: updatedOfficers
        };

        const updatedMap = {
            ...centersDetails,
            [selectedSlug]: updatedCenter
        };

        setCentersDetails(updatedMap);
        handleCancelOfficerEdit();

        const ok = await syncToServer(updatedMap);
        if (ok) {
            setMessage(editingOfficerId !== null ? '✅ නිලධාරියාගේ තොරතුරු සාර්ථකව යාවත්කාලීන විය!' : '✅ නව නිලධාරියා සාර්ථකව ඇතුළත් කර Save කරන ලදී!');
            setTimeout(() => setMessage(''), 4000);
        } else {
            setMessage('⚠ Local දත්ත සුරැකිණි, නමුත් සර්වර් එකට Save වීමේ දෝෂයක් ඇත.');
        }
    };

    // Delete Officer
    const handleDeleteOfficer = async (officerId) => {
        const currentCenter = centersDetails[selectedSlug];
        if (!currentCenter) return;

        const target = (currentCenter.officers || []).find(o => String(o.id) === String(officerId));
        const offName = target ? target.name : 'මෙම නිලධාරියා';

        if (window.confirm(`"${offName}" නිලධාරියා මෙම ආයතනයෙන් ඉවත් කිරීමට ඔබට සහතිකද?`)) {
            const updatedOfficers = (currentCenter.officers || []).filter(o => String(o.id) !== String(officerId));
            const updatedCenter = { ...currentCenter, officers: updatedOfficers };
            const updatedMap = { ...centersDetails, [selectedSlug]: updatedCenter };

            setCentersDetails(updatedMap);
            if (editingOfficerId && String(editingOfficerId) === String(officerId)) {
                handleCancelOfficerEdit();
            }

            const ok = await syncToServer(updatedMap);
            if (ok) {
                setMessage('✅ නිලධාරියා සාර්ථකව ඉවත් කරන ලදී!');
                setTimeout(() => setMessage(''), 3000);
            }
        }
    };

    // Add Brand New Center
    const handleCreateNewCenter = async (e) => {
        if (e) e.preventDefault();
        if (!newCenterForm.title.trim()) {
            setMessage('⚠ කරුණාකර ආයතනයේ නම ඇතුළත් කරන්න');
            return;
        }

        const slug = newCenterForm.slug.trim() || ('center-' + Date.now());
        const newObj = {
            slug: slug,
            title: newCenterForm.title.trim(),
            phone: newCenterForm.phone.trim(),
            email: newCenterForm.email.trim(),
            officers: []
        };

        const updatedMap = {
            ...centersDetails,
            [slug]: newObj
        };

        setCentersDetails(updatedMap);
        setSelectedSlug(slug);
        setShowNewCenterModal(false);
        setNewCenterForm({ title: '', slug: '', phone: '', email: '' });

        await syncToServer(updatedMap);
        setMessage('✅ නව ආයතනය සාර්ථකව නිර්මාණය විය! දැන් ඔබට නිලධාරීන් එක් කළ හැක.');
        setTimeout(() => setMessage(''), 4000);
    };

    const currentCenter = centersDetails[selectedSlug] || { officers: [] };
    const officersList = currentCenter.officers || [];

    const filteredOfficers = officersList.filter(o => {
        if (!officerSearch.trim()) return true;
        const q = officerSearch.toLowerCase();
        return (
            (o.name && o.name.toLowerCase().includes(q)) ||
            (o.position && o.position.toLowerCase().includes(q)) ||
            (o.phone && o.phone.toLowerCase().includes(q))
        );
    });

    return (
        <div className="manager-container" style={{ maxWidth: '1300px', margin: '0 auto' }}>
            {/* NOTIFICATION MESSAGE */}
            {message && (
                <div style={{
                    padding: '12px 20px',
                    background: message.startsWith('✅') ? '#dcfce7' : '#fee2e2',
                    color: message.startsWith('✅') ? '#15803d' : '#b91c1c',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    border: message.startsWith('✅') ? '1px solid #bbf7d0' : '1px solid #fecaca'
                }}>
                    {message}
                </div>
            )}

            {/* TOP BAR: CENTER SELECTOR */}
            <div style={{
                background: '#fff',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                marginBottom: '25px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div style={{ flex: 1, minWidth: '280px' }}>
                    <label style={{ display: 'block', fontWeight: '700', color: '#166534', marginBottom: '8px', fontSize: '0.95rem' }}>
                        🏢 කළමනාකරණය කිරීමට අවශ්‍ය ආයතනය හෝ මධ්‍යස්ථානය තෝරන්න (Select Center):
                    </label>
                    <select
                        value={selectedSlug}
                        onChange={(e) => setSelectedSlug(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #0b6b2f',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1e293b',
                            background: '#f0fdf4',
                            cursor: 'pointer'
                        }}
                    >
                        {Object.keys(centersDetails).map(slug => (
                            <option key={slug} value={slug}>
                                {centersDetails[slug].title || slug} ({centersDetails[slug].officers ? centersDetails[slug].officers.length : 0} නිලධාරීන්)
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="button"
                    onClick={() => setShowNewCenterModal(true)}
                    style={{
                        padding: '12px 20px',
                        background: '#0b6b2f',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.95rem',
                        boxShadow: '0 2px 6px rgba(11, 107, 47, 0.25)'
                    }}
                >
                    ➕ නව ආයතනයක් එකතු කරන්න (Add Center)
                </button>
            </div>

            {/* CENTER CONTACT & DETAILS BAR */}
            <div style={{
                background: '#fff',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                marginBottom: '25px',
                borderLeft: '5px solid #0b6b2f'
            }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#0b6b2f', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📞 සම්බන්ධ තොරතුරු සංස්කරණය ({currentCenter.title || selectedSlug})
                </h4>
                <form onSubmit={handleSaveCenterMeta} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '15px', alignItems: 'end' }}>
                    <div>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginBottom: '5px' }}>
                            ආයතනයේ නම (Center Title)
                        </label>
                        <input
                            type="text"
                            value={centerMeta.title}
                            onChange={(e) => setCenterMeta({ ...centerMeta, title: e.target.value })}
                            placeholder="නම ඇතුළත් කරන්න..."
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            required
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginBottom: '5px' }}>
                            දුරකතන (Phone)
                        </label>
                        <input
                            type="text"
                            value={centerMeta.phone}
                            onChange={(e) => setCenterMeta({ ...centerMeta, phone: e.target.value })}
                            placeholder="උදා: 045-2222505..."
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginBottom: '5px' }}>
                            ඊමේල් (Email)
                        </label>
                        <input
                            type="email"
                            value={centerMeta.email}
                            onChange={(e) => setCenterMeta({ ...centerMeta, email: e.target.value })}
                            placeholder="උදා: agrirathnapura@gmail.com..."
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                        />
                    </div>
                    <button
                        type="submit"
                        style={{
                            padding: '11px 22px',
                            background: '#15803d',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '0.95rem'
                        }}
                    >
                        💾 Save Center Info
                    </button>
                </form>
            </div>

            {/* MAIN TWO-COLUMN SECTION: OFFICER FORM & OFFICER LIST */}
            <div className="admin-grid" style={{ alignItems: 'flex-start' }}>
                {/* LEFT: OFFICER FORM */}
                <div className="form-section">
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '15px',
                        borderBottom: '2px solid #e2e8f0',
                        paddingBottom: '10px'
                    }}>
                        <h3 style={{ margin: 0, color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {editingOfficerId !== null ? '✏️ Edit Officer (නිලධාරියා සංස්කරණය)' : '➕ Add Officer (නව නිලධාරියෙකු ඇතුළත් කිරීම)'}
                        </h3>
                        {editingOfficerId !== null && (
                            <span style={{
                                background: '#fef08a',
                                color: '#854d0e',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.82rem',
                                fontWeight: 'bold'
                            }}>
                                සංස්කරණ මාදිලිය
                            </span>
                        )}
                    </div>

                    <div className="publish-form-wrapper">
                        <div className="form-group">
                            <label style={{ fontWeight: '600', color: '#334155' }}>
                                Officer Name (නිලධාරියාගේ නම) <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={officerForm.name}
                                onChange={(e) => setOfficerForm({ ...officerForm, name: e.target.value })}
                                placeholder="උදා: ජේ.පී.ගුණවර්ධන මයා..."
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            />
                        </div>

                        <div className="form-group">
                            <label style={{ fontWeight: '600', color: '#334155' }}>
                                Position (තනතුර) <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={officerForm.position}
                                onChange={(e) => setOfficerForm({ ...officerForm, position: e.target.value })}
                                placeholder="උදා: නියෝජ්‍ය කෘෂිකර්ම අධ්‍යක්ෂ..."
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            />
                        </div>

                        <div className="form-group">
                            <label style={{ fontWeight: '600', color: '#334155' }}>
                                Phone (දුරකථන අංකය)
                            </label>
                            <input
                                type="text"
                                value={officerForm.phone}
                                onChange={(e) => setOfficerForm({ ...officerForm, phone: e.target.value })}
                                placeholder="උදා: 071- 8150584..."
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            />
                        </div>

                        <div className="form-group">
                            <label style={{ fontWeight: '600', color: '#334155' }}>Photo (ඡායාරූපය)</label>
                            <div style={{ marginBottom: '10px' }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                    id="officerPhotoUpload"
                                />
                                <label
                                    htmlFor="officerPhotoUpload"
                                    style={{
                                        display: 'inline-block',
                                        padding: '9px 18px',
                                        background: '#15803d',
                                        color: '#fff',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    📷 {editingOfficerId && officerForm.image ? 'ඡායාරූපය වෙනස් කරන්න (Change Photo)' : 'ඡායාරූපයක් තෝරන්න (Choose Photo)'}
                                </label>
                                {(imagePreview || officerForm.image) && (
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        style={{
                                            marginLeft: '10px',
                                            padding: '9px 16px',
                                            background: '#ef4444',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        ✕ Remove
                                    </button>
                                )}
                            </div>

                            {(imagePreview || officerForm.image) && (
                                <div style={{
                                    marginTop: '10px',
                                    display: 'inline-block',
                                    padding: '5px',
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <img
                                        src={imagePreview || officerForm.image}
                                        alt="Officer Preview"
                                        style={{
                                            width: '120px',
                                            height: '140px',
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            display: 'block'
                                        }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <button
                                type="button"
                                className="login-btn"
                                onClick={(e) => handleSaveOfficer(e)}
                                disabled={!officerForm.name || !officerForm.position}
                                style={{
                                    flex: '1',
                                    padding: '12px 20px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    background: editingOfficerId !== null ? '#0284c7' : '#15803d',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: (!officerForm.name || !officerForm.position) ? 'not-allowed' : 'pointer',
                                    opacity: (!officerForm.name || !officerForm.position) ? 0.6 : 1
                                }}
                            >
                                {editingOfficerId !== null ? '💾 Update Officer (යාවත්කාලීන කරන්න)' : '➕ Add Officer (ඇතුළත් කරන්න)'}
                            </button>

                            {editingOfficerId !== null && (
                                <button
                                    type="button"
                                    onClick={handleCancelOfficerEdit}
                                    style={{
                                        padding: '12px 20px',
                                        background: '#64748b',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    ✕ Cancel
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT: OFFICERS LIST FOR SELECTED CENTER */}
                <div className="list-section">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '10px',
                        marginBottom: '15px'
                    }}>
                        <h3 style={{ margin: 0, color: '#1e293b' }}>
                            👥 නිලධාරීන් ({officersList.length}) - {currentCenter.title || selectedSlug}
                        </h3>
                        <input
                            type="text"
                            placeholder="🔍 නිලධාරියා සොයන්න..."
                            value={officerSearch}
                            onChange={(e) => setOfficerSearch(e.target.value)}
                            style={{
                                padding: '8px 14px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                fontSize: '0.88rem',
                                minWidth: '200px'
                            }}
                        />
                    </div>

                    {loading ? (
                        <p style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>Loading officers...</p>
                    ) : (
                        <div style={{ maxHeight: '650px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {filteredOfficers.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#888', padding: '30px', background: '#f8fafc', borderRadius: '8px' }}>
                                    {officerSearch ? 'සෙවුමට අදාළ නිලධාරීන් හමු නොවීය.' : 'මෙම ආයතනයේ නිලධාරීන් තවමත් ඇතුළත් කර නොමැත. වම්පස Form එක භාවිත කර නිලධාරීන් ඇතුළත් කරන්න.'}
                                </div>
                            ) : (
                                filteredOfficers.map((officer, index) => {
                                    const isBeingEdited = editingOfficerId !== null && String(editingOfficerId) === String(officer.id);

                                    return (
                                        <div
                                            key={officer.id || index}
                                            style={{
                                                padding: '14px',
                                                border: isBeingEdited ? '2px solid #0284c7' : '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                background: isBeingEdited ? '#eff6ff' : '#fff',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: '12px',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flex: 1, minWidth: '260px' }}>
                                                {officer.image ? (
                                                    <img
                                                        src={officer.image}
                                                        alt={officer.name}
                                                        style={{
                                                            width: '60px',
                                                            height: '65px',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e1'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div
                                                    style={{
                                                        width: '60px',
                                                        height: '65px',
                                                        background: '#e2e8f0',
                                                        color: '#475569',
                                                        borderRadius: '6px',
                                                        display: officer.image ? 'none' : 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 'bold',
                                                        fontSize: '1.2rem'
                                                    }}
                                                >
                                                    {officer.name ? officer.name.charAt(0) : '👤'}
                                                </div>

                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', color: '#0f172a', fontSize: '1rem', fontWeight: '700' }}>
                                                        {officer.name}
                                                    </h4>
                                                    <p style={{ margin: '0 0 4px 0', fontSize: '0.88rem', color: '#15803d', fontWeight: '600' }}>
                                                        {officer.position}
                                                    </p>
                                                    <small style={{ color: '#64748b', fontSize: '0.84rem' }}>
                                                        📞 {officer.phone || 'දුරකථන අංකයක් නැත'}
                                                    </small>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditOfficerClick(officer)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        padding: '8px 14px',
                                                        background: isBeingEdited ? '#0284c7' : '#f1f5f9',
                                                        color: isBeingEdited ? '#fff' : '#0f172a',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: '6px',
                                                        fontWeight: '600',
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    ✏️ {isBeingEdited ? 'Editing...' : 'Edit'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteOfficer(officer.id)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        padding: '8px 14px',
                                                        background: '#fee2e2',
                                                        color: '#b91c1c',
                                                        border: '1px solid #fecaca',
                                                        borderRadius: '6px',
                                                        fontWeight: '600',
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL: ADD NEW CENTER */}
            {showNewCenterModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '20px'
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        padding: '25px',
                        width: '100%',
                        maxWidth: '500px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                    }}>
                        <h3 style={{ margin: '0 0 15px 0', color: '#0b6b2f' }}>
                            ➕ නව ආයතනයක් එකතු කිරීම (Add New Center)
                        </h3>
                        <form onSubmit={handleCreateNewCenter} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.88rem', marginBottom: '5px' }}>
                                    ආයතනයේ නම (Title) *
                                </label>
                                <input
                                    type="text"
                                    value={newCenterForm.title}
                                    onChange={(e) => setNewCenterForm({ ...newCenterForm, title: e.target.value })}
                                    placeholder="උදා: කෘෂිකර්ම පුහුණු ආයතනය..."
                                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    required
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.88rem', marginBottom: '5px' }}>
                                    Slug / Key (පිටුවේ හැඳින්වීම)
                                </label>
                                <input
                                    type="text"
                                    value={newCenterForm.slug}
                                    onChange={(e) => setNewCenterForm({ ...newCenterForm, slug: e.target.value })}
                                    placeholder="උදා: agri-training-center (optional)"
                                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontWeight: '600', fontSize: '0.88rem', marginBottom: '5px' }}>දුරකතන</label>
                                    <input
                                        type="text"
                                        value={newCenterForm.phone}
                                        onChange={(e) => setNewCenterForm({ ...newCenterForm, phone: e.target.value })}
                                        placeholder="045-xxxxxxx"
                                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontWeight: '600', fontSize: '0.88rem', marginBottom: '5px' }}>ඊමේල්</label>
                                    <input
                                        type="email"
                                        value={newCenterForm.email}
                                        onChange={(e) => setNewCenterForm({ ...newCenterForm, email: e.target.value })}
                                        placeholder="center@gmail.com"
                                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                <button
                                    type="submit"
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: '#0b6b2f',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    💾 Create Center
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowNewCenterModal(false)}
                                    style={{
                                        padding: '12px 18px',
                                        background: '#64748b',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
